import React from 'react';
import { Text } from '../Typography';
import { Account } from '@/types';
import { cn } from '@/utils/cn';
import { colors } from '@/theme/colors';

export interface AccountListProps {
  accounts: Account[];
  selectedAccountId?: string | null;
  onSelect?: (account: Account) => void;
  title?: string;
  description?: string;
  className?: string;
  itemClassName?: string;
}

const colorByName = (name: string) => {
  const sum = Array.from(name).reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return colors.avatar[sum % colors.avatar.length];
};

export const AccountList: React.FC<AccountListProps> = ({
  accounts,
  selectedAccountId,
  onSelect,
  title = 'Company',
  description = 'Select company from your Aimes space',
  className,
  itemClassName,
}) => {
  const Item: React.FC<{ acc: Account; selected: boolean; noItemBorder?: boolean }> = ({
    acc,
    selected,
    noItemBorder,
  }) => (
    <button
      type="button"
      onClick={() => onSelect?.(acc)}
      className={cn(
        'w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-grey-light hover:dark:bg-grey-main h-[44px]',
        noItemBorder ? '' : 'border-b border-grey-plain dark:border-grey-main last:border-b-0',
        itemClassName
      )}
    >
      <div
        className="w-[15px] h-[15px] rounded-sm flex items-center justify-center shrink-0"
        style={{ backgroundColor: colorByName(acc.name) }}
      >
        <span className="text-white text-[10px] leading-[12px] font-bold">
          {acc.name.charAt(0).toUpperCase()}
        </span>
      </div>
      <div className="flex flex-col min-w-0 gap-1">
        <Text
          variant="text-md-regular"
          className="text-grey-main dark:text-grey-plain truncate block leading-[16px]"
        >
          {acc.name}
        </Text>
        {selected && (
          <Text
            variant="text-sm-regular"
            className="text-grey-pseudohover dark:text-grey-medium leading-[16px]"
          >
            Selected
          </Text>
        )}
      </div>
    </button>
  );

  // Ensure the selected account (if present) is rendered first, preserving the order of the rest
  const orderedAccounts: Account[] = selectedAccountId
    ? (() => {
        const idx = accounts.findIndex(a => a.id === selectedAccountId);
        if (idx < 0) return accounts;
        return [accounts[idx], ...accounts.slice(0, idx), ...accounts.slice(idx + 1)];
      })()
    : accounts;
  const firstIsSelected = orderedAccounts[0]?.id === selectedAccountId;

  return (
    <div
      className={cn(
        'w-[240px] max-w-[calc(100vw-16px)] bg-white dark:bg-w-[240px] max-w-[calc(100vw-16px)] bg-white dark:bg-grey-black rounded-lg border border-grey-plain dark:border-grey-main overflow-hiddenack rounded-lg overflow-hidden shadow-card',
        className
      )}
    >
      {/* Header */}
      <div className="px-4">
        <Text variant="text-md-bold" className="py-[14px] text-grey-main dark:text-grey-plain">
          {title}
        </Text>
        {description && (
          <Text variant="text-md-regular" className="text-grey-main dark:text-grey-plain pb-3">
            {description}
          </Text>
        )}
      </div>

      <ul className="py-2">
        {accounts.length === 0 && (
          <li className="px-4 py-3">
            <Text variant="text-md-regular" className="text-grey-medium">
              No companies
            </Text>
          </li>
        )}

        {orderedAccounts.map((acc, idx) => (
          <React.Fragment key={acc.id}>
            <li>
              <Item
                acc={acc}
                selected={selectedAccountId === acc.id}
                noItemBorder={firstIsSelected && idx === 0}
              />
            </li>
            {firstIsSelected && idx === 0 && (
              <li aria-hidden className="border-t border-grey-plain dark:border-grey-main" />
            )}
          </React.Fragment>
        ))}
      </ul>
    </div>
  );
};

export default AccountList;
